import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { InvoiceReconciliationPanel } from './InvoiceReconciliationPanel';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Send, 
  Clock, 
  Mail, 
  CreditCard, 
  TrendingDown,
  FileText,
  RefreshCw,
  File,
  Globe,
  Loader2
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import api, { showErrorToast } from '@/lib/api';
import { ROUTES } from '@/constants/routes';
import { useStore } from '@/lib/store';
import { ApplicationHistory } from './ApplicationHistory';
import { PeppolInvoiceActions } from './PeppolInvoiceActions';
import { PeppolAPI } from '@/services/peppol';
import { DocumentActions } from '@/components/common/DocumentActions';
import { LedesFormatDownload } from './LedesFormatDownload';
import {
  Invoice,
  EmailFormData,
  CreditNote,
  DownPayment
} from '@/types/invoicing';
import { formatCurrency, getStatusColor } from '@/utils/document';

interface InvoiceDetailsProps {
  invoice: Invoice;
  onUpdate: () => void;
  onClose?: () => void;
}

export function InvoiceDetails({ invoice, onUpdate }: InvoiceDetailsProps) {
  const { clients, matters, users } = useStore();
  const [availableCreditNotes, setAvailableCreditNotes] = useState<CreditNote[]>([]);
  const [availableDownPayments, setAvailableDownPayments] = useState<DownPayment[]>([]);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isCreditNoteDialogOpen, setIsCreditNoteDialogOpen] = useState(false);
  const [isDownPaymentDialogOpen, setIsDownPaymentDialogOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isSendingPeppol, setIsSendingPeppol] = useState(false);
  const [isPeppolDialogOpen, setIsPeppolDialogOpen] = useState(false);
  
  // Email form state
  const [emailForm, setEmailForm] = useState<EmailFormData>({
    message: '',
    additional_recipients: [],
    cc: [],
    bcc: []
  });
  
  // Application form state
  const [selectedCreditNote, setSelectedCreditNote] = useState<number | null>(null);
  const [selectedDownPayment, setSelectedDownPayment] = useState<number | null>(null);
  const [applicationAmount, setApplicationAmount] = useState<number>(0);
  const [applicationNotes, setApplicationNotes] = useState('');

  // Get client and matter info
  const client = clients.find(c => c.id === invoice.client);
  const matter = matters.find(m => m.id === invoice.matter);

  useEffect(() => {
    if (invoice.status !== 'paid') {
      fetchAvailableApplications();
    }
    generateDefaultEmailMessage();
  }, [invoice.id]);

  const fetchAvailableApplications = async () => {
    try {
      // Fetch available credit notes for this client/matter
      const creditNotesResponse = await api.get(
        `${ROUTES.API.CREDIT_NOTES}?client=${invoice.client}&status=issued`
      );
      setAvailableCreditNotes(creditNotesResponse.data.results || []);

      // Fetch available down payments for this client/matter  
      const downPaymentsResponse = await api.get(
        `${ROUTES.API.DOWN_PAYMENTS}?client=${invoice.client}&status=issued`
      );
      setAvailableDownPayments(downPaymentsResponse.data.results || []);
    } catch (error) {
      console.error('Failed to fetch available applications:', error);
    }
  };

  const generateDefaultEmailMessage = () => {
    const clientName = client?.name || 'Client';
    const matterName = matter?.group_name 
      ? `${matter.group_name} - ${matter.name}` 
      : matter?.name || 'Matter';
    
    const defaultMessage = `Dear ${clientName},

Please find attached Invoice ${invoice.invoice_number} dated ${format(parseISO(invoice.issue_date), 'dd/MM/yyyy')} for services rendered in relation to matter: ${matterName}.

The total amount of €${Number(invoice.total || 0).toFixed(2)} is due for payment by ${format(parseISO(invoice.due_date), 'dd/MM/yyyy')}.

Should you have any queries regarding this invoice, please do not hesitate to contact us.

Thank you for attending to this matter at your earliest convenience.

Best regards,`;

    setEmailForm(prev => ({ ...prev, message: defaultMessage }));
  };

  const handleSendEmail = async () => {
    setIsSendingEmail(true);
    try {
      await api.post(`${ROUTES.API.INVOICES}${invoice.id}/send-email/`, emailForm);
      toast({
        title: 'Success',
        description: 'Invoice email sent successfully'
      });
      setIsEmailDialogOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to send email:', error);
      toast({
        title: 'Error',
        description: 'Failed to send invoice email',
        variant: 'destructive'
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSendPeppol = async () => {
    setIsSendingPeppol(true);
    try {
      const result = await PeppolAPI.sendInvoiceViaPeppol(invoice.id, { validate_first: true });
      if (result.success) {
        toast({
          title: 'Invoice Sent',
          description: result.message || 'Invoice submitted to Peppol network',
        });
        setIsPeppolDialogOpen(false);
        onUpdate();
      } else {
        toast({
          title: 'Send Failed',
          description: result.error || 'Failed to send invoice via Peppol',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Send Failed',
        description: error.response?.data?.error || error.response?.data?.detail || 'Failed to send invoice via Peppol',
        variant: 'destructive',
      });
    } finally {
      setIsSendingPeppol(false);
    }
  };

  const handleApplyCreditNote = async () => {
    if (!selectedCreditNote || applicationAmount <= 0) return;
    
    setIsApplying(true);
    try {
      await api.post(`${ROUTES.API.CREDIT_NOTES}${selectedCreditNote}/apply-to-invoice/`, {
        invoice_id: invoice.id,
        amount_to_apply: applicationAmount,
        notes: applicationNotes
      });
      
      toast({
        title: 'Success',
        description: 'Credit note applied successfully'
      });
      
      setIsCreditNoteDialogOpen(false);
      fetchAvailableApplications();
      onUpdate();
    } catch (error) {
      console.error('Failed to apply credit note:', error);
      toast({
        title: 'Error',
        description: 'Failed to apply credit note',
        variant: 'destructive'
      });
    } finally {
      setIsApplying(false);
    }
  };

  const handleApplyDownPayment = async () => {
    if (!selectedDownPayment || applicationAmount <= 0) return;
    
    setIsApplying(true);
    try {
      await api.post(`${ROUTES.API.DOWN_PAYMENTS}${selectedDownPayment}/apply-to-invoice/`, {
        invoice_id: invoice.id,
        amount_to_apply: applicationAmount,
        notes: applicationNotes
      });
      
      toast({
        title: 'Success',
        description: 'Down payment applied successfully'
      });
      
      setIsDownPaymentDialogOpen(false);
      fetchAvailableApplications();
      onUpdate();
    } catch (error) {
      console.error('Failed to apply down payment:', error);
      toast({
        title: 'Error',
        description: 'Failed to apply down payment',
        variant: 'destructive'
      });
    } finally {
      setIsApplying(false);
    }
  };

  const handleDownloadDocument = async () => {
    try {
      // Get the token the same way the api instance does
      const token = localStorage.getItem('accessToken');
      const baseURL = import.meta.env.VITE_API_URL || 'https://go.sentra.be';
      const downloadUrl = `${baseURL}${ROUTES.API.INVOICES}${invoice.id}/document/`;
      
      // Use fetch with manual redirect handling
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        redirect: 'manual'
      });
      
      if (response.status === 302) {
        // Get the redirect location
        const location = response.headers.get('location');
        if (location) {
          // Create a temporary anchor element to trigger the download
          const link = document.createElement('a');
          link.href = location;
          link.download = ''; // This will use the filename from the URL
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          toast({
            title: 'Success',
            description: 'Document download started'
          });
        } else {
          throw new Error('No redirect location found');
        }
      } else if (response.ok) {
        // Direct file response
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        
        // Try to get filename from content-disposition header
        const contentDisposition = response.headers.get('content-disposition');
        let fileName = `invoice_${invoice.id}_document`;
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch) {
            fileName = fileNameMatch[1];
          }
        }
        
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        
        toast({
          title: 'Success',
          description: 'Document downloaded successfully'
        });
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
    } catch (error) {
      console.error('Document download error:', error);
      toast({
        title: 'Error',
        description: 'Failed to download original document',
        variant: 'destructive'
      });
    }
  };

  const handleDownloadPDF = async () => {
    try {
      // Get the token the same way the api instance does
      const token = localStorage.getItem('accessToken');
      const baseURL = import.meta.env.VITE_API_URL || 'https://go.sentra.be';
      const downloadUrl = `${baseURL}${ROUTES.API.INVOICES}${invoice.id}/pdf/`;
      
      // Use fetch with manual redirect handling
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        redirect: 'manual'
      });
      
      if (response.status === 302) {
        // Get the redirect location
        const location = response.headers.get('location');
        if (location) {
          // Create a temporary anchor element to trigger the download
          const link = document.createElement('a');
          link.href = location;
          link.download = ''; // This will use the filename from the URL
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          toast({
            title: 'Success',
            description: 'PDF download started'
          });
        } else {
          throw new Error('No redirect location found');
        }
      } else if (response.ok) {
        // Direct blob response
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', `invoice_${invoice.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        
        toast({
          title: 'Success',
          description: 'PDF downloaded successfully'
        });
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

    } catch (error) {
      console.error('PDF generation error:', error);
      showErrorToast(error, 'Error', 'Failed to generate PDF');
    }
  };

  const handleDownloadTimesheets = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const baseURL = import.meta.env.VITE_API_URL || 'https://go.sentra.be';
      const url = `${baseURL}${ROUTES.API.INVOICES}${invoice.id}/download_timesheets/`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.setAttribute('download', `timesheets_invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      toast({ title: 'Success', description: 'Timesheets downloaded successfully' });
    } catch (error) {
      console.error('Timesheet download error:', error);
      showErrorToast(error, 'Error', 'Failed to download timesheets');
    }
  };

  const handleRegenerateDocument = async () => {
    try {
      await api.post(`${ROUTES.API.INVOICES}${invoice.id}/regenerate/`);
      
      toast({
        title: 'Success',
        description: 'Invoice has been regenerated with current data'
      });
      
      // Refresh the invoice data
      onUpdate();
    } catch (error) {
      console.error('Document regeneration error:', error);
      showErrorToast(error, 'Error', 'Failed to regenerate document');
    }
  };


  const getUserName = (userId: number) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : 'Unknown User';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold">Invoice #{invoice.invoice_number}</h2>
          <p className="text-muted-foreground">
            {client?.name} - {matter?.group_name ? `${matter.group_name} - ${matter.name}` : matter?.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={getStatusColor(invoice.status)}>
            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
          </Badge>
          {invoice.email_sent_at && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              Email sent
            </Badge>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-4">
        {/* Document Management */}
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Document Management</h3>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleDownloadDocument} variant="outline">
              <File className="h-4 w-4 mr-2" />
              Download Original
            </Button>
            
            <Button onClick={handleDownloadPDF} variant="outline">
              <FileText className="h-4 w-4 mr-2" />
              Generate PDF
            </Button>
            
            <Button onClick={handleDownloadTimesheets} variant="outline">
              <Clock className="h-4 w-4 mr-2" />
              Download Timesheets
            </Button>

            <Button onClick={handleRegenerateDocument} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Regenerate
            </Button>
          </div>
        </div>

        {/* Export & Communication */}
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Export & Communication</h3>
          <div className="flex gap-2 flex-wrap">
            <LedesFormatDownload invoiceId={invoice.id} />
            
            <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Send className="h-4 w-4 mr-2" />
                  Send Email
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Send Invoice Email</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={emailForm.message}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
                  rows={12}
                  className="font-mono text-sm whitespace-pre-wrap"
                  style={{ whiteSpace: 'pre-wrap' }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEmailDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmail} disabled={isSendingEmail}>
                {isSendingEmail ? 'Sending...' : 'Send Email'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

            <Dialog open={isPeppolDialogOpen} onOpenChange={setIsPeppolDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Globe className="h-4 w-4 mr-2" />
                  Send via Peppol
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Send Invoice via Peppol</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  This will send invoice {invoice.invoice_number} to {client?.name || 'the client'} via the Peppol network.
                  The invoice will be validated before sending.
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsPeppolDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSendPeppol} disabled={isSendingPeppol}>
                    {isSendingPeppol ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Globe className="h-4 w-4 mr-2" />
                    )}
                    {isSendingPeppol ? 'Sending...' : 'Send via Peppol'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
        </div>
      </div>

      {/* Status and Delete Actions */}
      <DocumentActions
          documentType="invoice"
          documentId={invoice.id}
          documentNumber={invoice.invoice_number}
          currentStatus={invoice.status}
          onStatusChanged={onUpdate}
          onDeleted={() => {
            onUpdate();
            // Navigate back or close modal if needed
          }}
        />

        {invoice.status !== 'paid' && (
          <>
            <Dialog open={isCreditNoteDialogOpen} onOpenChange={setIsCreditNoteDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Apply Credit Note
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Apply Credit Note</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Select Credit Note</Label>
                    <select 
                      className="w-full p-2 border rounded"
                      value={selectedCreditNote || ''}
                      onChange={(e) => setSelectedCreditNote(Number(e.target.value))}
                    >
                      <option value="">Select a credit note...</option>
                      {availableCreditNotes.map(cn => (
                        <option key={cn.id} value={cn.id}>
                          {cn.credit_note_number} - {formatCurrency(Number(cn.remaining_amount) || 0)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="amount">Amount to Apply</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={applicationAmount}
                      onChange={(e) => setApplicationAmount(Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={applicationNotes}
                      onChange={(e) => setApplicationNotes(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreditNoteDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleApplyCreditNote} disabled={isApplying}>
                    {isApplying ? 'Applying...' : 'Apply Credit Note'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={isDownPaymentDialogOpen} onOpenChange={setIsDownPaymentDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <TrendingDown className="h-4 w-4 mr-2" />
                  Apply Down Payment
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Apply Down Payment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Select Down Payment</Label>
                    <select 
                      className="w-full p-2 border rounded"
                      value={selectedDownPayment || ''}
                      onChange={(e) => setSelectedDownPayment(Number(e.target.value))}
                    >
                      <option value="">Select a down payment...</option>
                      {availableDownPayments.map(dp => (
                        <option key={dp.id} value={dp.id}>
                          {dp.payment_number} - {formatCurrency(Number(dp.remaining_amount) || 0)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="amount">Amount to Apply</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={applicationAmount}
                      onChange={(e) => setApplicationAmount(Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={applicationNotes}
                      onChange={(e) => setApplicationNotes(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsDownPaymentDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleApplyDownPayment} disabled={isApplying}>
                    {isApplying ? 'Applying...' : 'Apply Down Payment'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>

      {/* Invoice Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Issue Date:</span>
              <span>{format(parseISO(invoice.issue_date), 'dd/MM/yyyy')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Due Date:</span>
              <span>{format(parseISO(invoice.due_date), 'dd/MM/yyyy')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Amount:</span>
              <span className="font-semibold">{formatCurrency(Number(invoice.total) || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Time Entries:</span>
              <span>{invoice.timesheets?.length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Expenses:</span>
              <span>{invoice.expenses?.length || 0}</span>
            </div>
            {invoice.notes && (
              <>
                <Separator />
                <div>
                  <span className="text-muted-foreground">Notes:</span>
                  <p className="mt-1 text-sm">{invoice.notes}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Application History - using new component */}
        <ApplicationHistory invoiceId={invoice.id} />
      </div>

      {/* Peppol E-Invoicing */}
      <PeppolInvoiceActions
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoice_number}
        clientName={client?.name}
        onUpdate={onUpdate}
      />

      {/* Email History */}
      {invoice.email_sent_at && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-green-600">
                <Mail className="h-4 w-4" />
                <span className="font-medium">Email sent</span>
              </div>
              <span className="text-muted-foreground">
                {invoice.email_sent_at ? format(parseISO(invoice.email_sent_at as string), 'dd/MM/yyyy HH:mm') : 'N/A'}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Time Entries and Expenses Tables */}
      {(invoice.timesheets?.length > 0 || invoice.expenses?.length > 0) && (
        <div className="space-y-6">
          {invoice.timesheets?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Time Entries ({invoice.timesheets.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoice.timesheets.map((timesheet) => (
                        <TableRow key={timesheet.id}>
                          <TableCell>{format(parseISO(timesheet.date), 'dd/MM/yyyy')}</TableCell>
                          <TableCell>{getUserName(timesheet.user)}</TableCell>
                          <TableCell>{timesheet.description}</TableCell>
                          <TableCell className="text-right">{timesheet.billable_hours}</TableCell>
                          <TableCell className="text-right">{formatCurrency(Number(timesheet.fee) || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {invoice.expenses?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Expenses ({invoice.expenses.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoice.expenses.map((expense) => (
                        <TableRow key={expense.id}>
                          <TableCell>{format(parseISO(expense.date), 'dd/MM/yyyy')}</TableCell>
                          <TableCell>{expense.category}</TableCell>
                          <TableCell>{expense.description}</TableCell>
                          <TableCell className="text-right">{formatCurrency(Number(expense.amount) || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Authoritative reconciliation view (snapshots + periods + attach) */}
          {((invoice as any).invoice_type ?? 'invoice') === 'invoice' && (
            <Card>
              <CardHeader>
                <CardTitle>Reconciliation</CardTitle>
              </CardHeader>
              <CardContent>
                <InvoiceReconciliationPanel
                  invoice={invoice as unknown as import('./InvoiceReconciliationPanel').InvoiceForReconciliation}
                  onRefresh={onUpdate}
                />
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
